import type { CaseStepDetail } from '@/lib/api/quality-types.js';

import { boundToClip, type ClipWindow } from './recordings.js';

/** One step surfaced as a chapter under the docked player, scoped to the bound case (never the
 *  whole session). Steps with no `timecode_ms`, or one outside the bound clip window, never become
 *  a chapter (AC 23). */
export interface Chapter {
  timecodeMs: number;
  name: string;
  status: string;
  /** To the next chapter's timecode, or the step's own `duration_ms` when it is the last chapter.
   *  Null when neither is known — a run whose fragment predates per-step timing. */
  durationMs: number | null;
}

/** A step's own wall-clock duration, parsed the same way as the rest of the report (`duration_ms` is
 *  string-or-number on the wire); null when absent or not a real number. */
function parseStepDurationMs(
  value: number | string | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The bound case's steps, turned into chapters (AC 18): timed, in-window, sorted ascending. A step
 * with no `timecode_ms` is dropped entirely, not rendered at 0 (AC 23). `window` bounds a mobile
 * session's shared timeline down to this case's own slice; a step outside it is dropped the same
 * way as one with no timecode — `null` window (Branch B, or a web recording) keeps every timed step
 * unfiltered.
 */
export function buildChapters(
  steps: CaseStepDetail[],
  window: ClipWindow | null,
): Chapter[] {
  const timedSteps = steps
    .map((step) => ({
      step,
      timecodeMs: boundToClip(step.timecode_ms, window),
    }))
    .filter(
      (entry): entry is { step: CaseStepDetail; timecodeMs: number } =>
        entry.timecodeMs !== null,
    )
    .sort((a, b) => a.timecodeMs - b.timecodeMs);

  return timedSteps.map((entry, index) => {
    const nextEntry = timedSteps[index + 1];
    return {
      timecodeMs: entry.timecodeMs,
      name: entry.step.name,
      status: entry.step.status,
      durationMs: nextEntry
        ? nextEntry.timecodeMs - entry.timecodeMs
        : parseStepDurationMs(entry.step.duration_ms),
    };
  });
}

/**
 * The active chapter for a `timeupdate` tick (AC 19): the last index whose timecode is `<=
 * currentTimeMs`, `-1` before the first chapter. Binary search over the pre-sorted array — the
 * caller (`PlayerDock`) ticks this on every `timeupdate`, so it must stay cheap and must only ever
 * be used to decide whether the resolved index actually changed before calling `setState`.
 */
export function activeChapterIndex(
  sortedTimecodesMs: number[],
  currentTimeMs: number,
): number {
  let low = 0;
  let high = sortedTimecodesMs.length - 1;
  let activeIndex = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const midTimecodeMs = sortedTimecodesMs[mid];
    if (midTimecodeMs === undefined) break;
    if (midTimecodeMs <= currentTimeMs) {
      activeIndex = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return activeIndex;
}

/** Where a chapter click should seek (AC 20), honouring the clip window it was built against — null
 *  when the chapter's own timecode no longer falls inside `window`, in which case the caller must
 *  not seek. */
export function chapterSeekMs(
  chapter: Chapter,
  window: ClipWindow | null,
): number | null {
  return boundToClip(chapter.timecodeMs, window);
}

/**
 * What a chapter row shows for a step whose name opens with a boilerplate prefix. Real suites start
 * every setup step with `Precondition: …`, so a truncated list repeats the same fourteen characters
 * and the distinguishing part never fits. This strips the prefix for DISPLAY only — the chapter's
 * `name` still drives the accessible label and nothing about seeking changes — and a step named
 * nothing but the prefix keeps its original text instead of collapsing to an empty row.
 */
export function chapterLabel(name: string): string {
  const stripped = name.replace(/^\s*precondition\s*[:.–—-]\s*/i, '').trim();
  return stripped.length > 0 ? stripped : name;
}
